import { emailsInfiniteQuery } from "@common/api"
import { useMaxWidth } from "@common/hooks/use-mobile"
import {
  createFileRoute,
  Outlet,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import { createContext, useContext, useState } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { EmailTable, type Picked } from "./-components/email-table"
import { ForwardingAddress } from "./-components/forwarding-address"
import { COLUMN_SIZING, COLUMNS } from "./-lib/columns"
import {
  emailFilterConfigs,
  hasEmailFilters,
  NO_EMAIL_FILTERS,
} from "./-lib/filters"
import {
  type EmailsSearch,
  emailPagesParams,
  emailsSearchSchema,
  rememberTab,
  TABS,
  tabLabel,
  tabOf,
} from "./-lib/search"

type EmailsSelectionContextValue = {
  clearEmailSelection: () => void
}

const EmailsSelectionContext =
  createContext<EmailsSelectionContextValue | null>(null)

export function useEmailsSelectionContext(): EmailsSelectionContextValue {
  const context = useContext(EmailsSelectionContext)
  if (context === null) {
    throw new Error("useEmailsSelectionContext requires EmailsSelectionContext")
  }
  return context
}

export const Route = createFileRoute("/_layout/quoting_/emails/_list")({
  validateSearch: (search) => emailsSearchSchema.parse(search),
  loaderDeps: ({ search }) => emailPagesParams(search),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureInfiniteQueryData(emailsInfiniteQuery(deps)),
  component: EmailsPage,
})

function useUpdateSearch(): (patch: Partial<EmailsSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function EmailsPage(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(COLUMNS, COLUMN_SIZING)
  const fit = fitColumns([...COLUMNS], width, sizing)
  const open = useMatches({
    select: (matches) =>
      matches.some((match) => match.staticData.detailSidebar),
  })
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const [picked, setPicked] = useState<Picked>(new Set())

  return (
    <EmailsSelectionContext.Provider
      value={{ clearEmailSelection: () => setPicked(new Set()) }}
    >
      <ListPage
        toolbar={<Toolbar panelOpen={open} />}
        pane={open && !compact ? "split" : "closed"}
        detail={<Outlet />}
        scrollRef={ref}
      >
        <ActiveFilters />
        <EmailTable
          search={search}
          columns={fit.shown}
          picked={picked}
          onPick={setPicked}
          onClear={() => update({ ...NO_EMAIL_FILTERS, search: undefined })}
        />
      </ListPage>
    </EmailsSelectionContext.Provider>
  )
}

function Toolbar({ panelOpen }: { panelOpen: boolean }): React.ReactElement {
  const values = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tabOf(values)}
          label={tabLabel}
          onSelect={(next) => {
            rememberTab(next)
            update({ tab: next })
          }}
        />
      }
      actions={
        <>
          {!panelOpen && <ForwardingAddress />}
          <NameFilter
            search={values.search ?? ""}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search emails…"
          />
          <FilterButton
            active={hasEmailFilters(values)}
            configs={emailFilterConfigs}
            onChange={update}
          />
        </>
      }
    />
  )
}

function ActiveFilters(): React.ReactElement {
  const { sender, period, from, to } = Route.useSearch()
  const update = useUpdateSearch()
  const values = { sender, period, from, to }

  return (
    <ActiveFilterBar
      active={hasEmailFilters(values)}
      configs={emailFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_EMAIL_FILTERS)}
    />
  )
}
